import { TenantTheme } from '../contexts/TenantContext';

export interface ThemeLoadOptions {
  useCache?: boolean;
  fallbackToDefault?: boolean;
}

export class ThemeService {
  private static instance: ThemeService;
  private themeCache: Map<string, { theme: TenantTheme; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  /**
   * Load theme configuration for a tenant
   */
  public async loadTenantTheme(
    tenantKey: string, 
    options: ThemeLoadOptions = {}
  ): Promise<TenantTheme> {
    const { useCache = true, fallbackToDefault = true } = options;

    try {
      // Check cache first
      if (useCache) {
        const cached = this.getCachedTheme(tenantKey);
        if (cached) {
          return cached;
        }
      }

      // Load theme based on tenant key
      let theme: TenantTheme;

      if (tenantKey === 'cpv.cl') {
        theme = this.getCPVTheme();
      } else {
        theme = await this.loadThemeFromAPI(tenantKey, fallbackToDefault);
      }

      // Cache the theme
      if (useCache) {
        this.cacheTheme(tenantKey, theme);
      }

      return theme;

    } catch (error) {
      console.error(`Error loading theme for tenant ${tenantKey}:`, error);
      
      if (fallbackToDefault) {
        return this.getDefaultTheme(tenantKey);
      }
      
      throw error;
    }
  }

  /**
   * Apply theme to document root
   */
  public applyTheme(theme: TenantTheme): void {
    const root = document.documentElement;
    
    // Apply CSS custom properties for colors
    root.style.setProperty('--color-primary', theme.branding.colors.primary);
    root.style.setProperty('--color-secondary', theme.branding.colors.secondary);
    
    if (theme.branding.colors.accent) {
      root.style.setProperty('--color-accent', theme.branding.colors.accent);
    }
    
    if (theme.branding.colors.background) {
      root.style.setProperty('--color-background', theme.branding.colors.background);
    }
    
    if (theme.branding.colors.text) {
      root.style.setProperty('--color-text', theme.branding.colors.text);
    }

    // Apply typography
    if (theme.branding.typography?.fontFamily) {
      root.style.setProperty('--font-family', theme.branding.typography.fontFamily);
    }

    if (theme.branding.typography?.headingFont) {
      root.style.setProperty('--font-heading', theme.branding.typography.headingFont);
    }

    // Update favicon
    this.updateFavicon(theme.branding.faviconUrl);

    // Update page title
    document.title = `${theme.displayName} - Sistema de Evaluación Integral`;

    console.log(`✅ Theme applied for: ${theme.displayName}`);
  }

  /**
   * Get CPV-specific theme configuration
   */
  private getCPVTheme(): TenantTheme {
    return {
      displayName: 'Colegio Puerto Varas',
      branding: {
        logoUrl: '/images/logo.png',
        faviconUrl: '/favicon.svg',
        colors: {
          primary: '#3B82F6',
          secondary: '#10B981',
          accent: '#F59E0B',
          background: '#FFFFFF',
          text: '#1F2937'
        },
        typography: {
          fontFamily: 'Inter, system-ui, sans-serif',
          headingFont: 'Inter, system-ui, sans-serif'
        }
      },
      customization: {
        showWelcomeMessage: true,
        welcomeMessage: 'Bienvenido al Sistema de Evaluación Integral - Colegio Puerto Varas',
        footerText: 'Colegio Puerto Varas - Sistema de Evaluación Integral',
        supportEmail: 'soporte@cpv.cl',
        supportPhone: '+56 65 2234567'
      }
    };
  }

  /**
   * Load theme from API for non-CPV tenants
   */
  private async loadThemeFromAPI(tenantKey: string, fallbackToDefault: boolean): Promise<TenantTheme> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`/api/tenant/${tenantKey}/theme`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404 && fallbackToDefault) {
        return this.getDefaultTheme(tenantKey);
      }
      throw new Error(`Failed to load tenant theme: ${response.statusText}`);
    }

    const data = await response.json();
    return data.theme || this.getDefaultTheme(tenantKey);
  }

  /**
   * Get default theme with tenant-specific display name
   */
  private getDefaultTheme(tenantKey: string): TenantTheme {
    const displayName = tenantKey ? 
      tenantKey.split('.')[0].charAt(0).toUpperCase() + tenantKey.split('.')[0].slice(1) :
      'Colegio Sin Notas';

    return {
      displayName,
      branding: {
        logoUrl: '/images/default-logo.png',
        faviconUrl: '/favicon.svg',
        colors: {
          primary: '#3B82F6',
          secondary: '#10B981',
          accent: '#F59E0B',
          background: '#FFFFFF',
          text: '#1F2937'
        },
        typography: {
          fontFamily: 'Inter, system-ui, sans-serif',
          headingFont: 'Inter, system-ui, sans-serif'
        }
      },
      customization: {
        showWelcomeMessage: true,
        welcomeMessage: `Bienvenido al Sistema de Evaluación Integral - ${displayName}`,
        footerText: `${displayName} - Sistema de Evaluación Integral`,
        supportEmail: 'soporte@colegio.cl',
        supportPhone: '+56 9 1234 5678'
      }
    };
  }

  /**
   * Get cached theme if available and not expired
   */
  private getCachedTheme(tenantKey: string): TenantTheme | null {
    const cached = this.themeCache.get(tenantKey);
    
    if (!cached) {
      return null;
    }

    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      this.themeCache.delete(tenantKey);
      return null;
    }

    return cached.theme;
  }

  /**
   * Cache theme with timestamp
   */
  private cacheTheme(tenantKey: string, theme: TenantTheme): void {
    this.themeCache.set(tenantKey, {
      theme,
      timestamp: Date.now()
    });
  }

  /**
   * Update favicon dynamically
   */
  private updateFavicon(faviconUrl: string): void {
    try {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon && faviconUrl) {
        favicon.href = faviconUrl;
      }
    } catch (error) {
      console.warn('Failed to update favicon:', error);
    }
  }

  /**
   * Clear theme cache
   */
  public clearCache(tenantKey?: string): void {
    if (tenantKey) {
      this.themeCache.delete(tenantKey);
    } else {
      this.themeCache.clear();
    }
  }

  /**
   * Preload themes for multiple tenants
   */
  public async preloadThemes(tenantKeys: string[]): Promise<void> {
    const loadPromises = tenantKeys.map(tenantKey => 
      this.loadTenantTheme(tenantKey, { useCache: true, fallbackToDefault: true })
        .catch(error => {
          console.warn(`Failed to preload theme for ${tenantKey}:`, error);
          return null;
        })
    );

    await Promise.all(loadPromises);
    console.log(`✅ Preloaded themes for ${tenantKeys.length} tenants`);
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.themeCache.size,
      keys: Array.from(this.themeCache.keys())
    };
  }
}

export default ThemeService;